import React, { useState, useEffect } from 'react';
import { Bell, AlertCircle, Info, Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const NewsTicker = () => {
  const { theme } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Demo-Nachrichten - später aus Supabase
  const newsItems = [
    {
      id: 1,
      type: 'maintenance',
      icon: AlertCircle,
      message: 'Wartungsarbeiten am 15.08. von 9-12 Uhr - Wasserversorgung kurzzeitig unterbrochen',
      priority: 'high',
      timestamp: '2025-08-14T10:00:00Z'
    },
    {
      id: 2,
      type: 'document',
      icon: Bell,
      message: 'Neue Nebenkostenabrechnung 2024 verfügbar - Jetzt in Ihren Dokumenten einsehen',
      priority: 'medium',
      timestamp: '2025-08-13T14:30:00Z'
    },
    {
      id: 3,
      type: 'event',
      icon: Calendar,
      message: 'Hausversammlung am 20.08. um 19 Uhr im Gemeinschaftsraum - Alle Mieter herzlich eingeladen',
      priority: 'medium',
      timestamp: '2025-08-12T16:15:00Z'
    },
    {
      id: 4,
      type: 'info',
      icon: Info,
      message: 'Neue Mülltonnen wurden aufgestellt - Bitte beachten Sie die aktualisierten Entsorgungszeiten',
      priority: 'low',
      timestamp: '2025-08-11T09:45:00Z'
    },
    {
      id: 5,
      type: 'maintenance',
      icon: AlertCircle,
      message: 'Aufzug in Haus B wird am 18.08. gewartet - Bitte nutzen Sie das Treppenhaus',
      priority: 'high',
      timestamp: '2025-08-10T11:20:00Z'
    }
  ];

  // Auto-scroll durch Nachrichten
  useEffect(() => {
    if (newsItems.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === newsItems.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Wechsel alle 4 Sekunden

    return () => clearInterval(interval);
  }, [newsItems.length]);

  // Theme-spezifische Klassen
  const getThemeClasses = () => {
    switch (theme) {
      case 'light':
        return {
          background: 'bg-blue-50 border-blue-200',
          text: 'text-blue-900',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600'
        };
      case 'dark':
        return {
          background: 'bg-gray-800 border-gray-600',
          text: 'text-gray-100',
          iconBg: 'bg-gray-700',
          iconColor: 'text-blue-400'
        };
      default:
        return {
          background: 'bg-blue-50 border-blue-200',
          text: 'text-blue-900',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600'
        };
    }
  };

  const themeClasses = getThemeClasses();

  // Prioritäts-spezifische Farben
  const getPriorityClasses = (priority) => {
    switch (priority) {
      case 'high':
        return {
          background: theme === 'light' ? 'bg-red-50 border-red-200' : 
                     theme === 'dark' ? 'bg-red-900/20 border-red-700' : 'bg-red-800/30 border-red-600',
          text: theme === 'light' ? 'text-red-900' : 'text-red-200',
          iconBg: theme === 'light' ? 'bg-red-100' : 
                  theme === 'dark' ? 'bg-red-800' : 'bg-red-700',
          iconColor: theme === 'light' ? 'text-red-600' : 'text-red-300'
        };
      case 'medium':
        return {
          background: theme === 'light' ? 'bg-yellow-50 border-yellow-200' : 
                     theme === 'dark' ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-800/30 border-yellow-600',
          text: theme === 'light' ? 'text-yellow-900' : 'text-yellow-200',
          iconBg: theme === 'light' ? 'bg-yellow-100' : 
                  theme === 'dark' ? 'bg-yellow-800' : 'bg-yellow-700',
          iconColor: theme === 'light' ? 'text-yellow-600' : 'text-yellow-300'
        };
      default:
        return themeClasses;
    }
  };

  if (newsItems.length === 0) {
    return null;
  }

  const currentNews = newsItems[currentIndex];
  const Icon = currentNews.icon;
  const priorityClasses = getPriorityClasses(currentNews.priority);

  return (
    <div className={`
      ${priorityClasses.background} border rounded-lg p-3 mb-6 
      transition-all duration-500 ease-in-out
    `}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className={`
          ${priorityClasses.iconBg} p-2 rounded-full flex-shrink-0
          transition-all duration-500
        `}>
          <Icon className={`w-4 h-4 ${priorityClasses.iconColor}`} />
        </div>

        {/* Nachricht */}
        <div className="flex-1 min-w-0">
          <p className={`
            ${priorityClasses.text} text-sm font-medium leading-relaxed
            transition-all duration-500
          `}>
            {currentNews.message}
          </p>
        </div>

        {/* Indikator-Punkte */}
        <div className="flex space-x-1 flex-shrink-0">
          {newsItems.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`
                w-2 h-2 rounded-full transition-all duration-300
                ${index === currentIndex 
                  ? priorityClasses.iconColor.replace('text-', 'bg-')
                  : theme === 'light' ? 'bg-gray-300' : 'bg-gray-600'
                }
                hover:scale-125
              `}
            />
          ))}
        </div>
      </div>

      {/* Progress Bar */}
      <div className={`
        mt-2 h-1 ${theme === 'light' ? 'bg-gray-200' : 'bg-gray-700'} rounded-full overflow-hidden
      `}>
        <div 
          className={`
            h-full ${priorityClasses.iconColor.replace('text-', 'bg-')} rounded-full
            transition-all duration-[4000ms] ease-linear
          `}
          style={{
            width: '100%',
            animation: 'progress 4s linear infinite'
          }}
        />
      </div>

      <style jsx>{`
        @keyframes progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default NewsTicker;
